import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Mail, MapPin, Phone, QrCode, ShieldCheck, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const img = (n) => `${API}/public/promo-image/${n}`;

import { FEATURES } from '../lib/features';
const STEPS = [
  ['Firma kaydolur', 'Belgelerini yükler, İSG birimi onaylar.'],
  ['Talep oluşturulur', '5 bölümlü akıllı sihirbazla personel, ekipman ve belgeler eklenir.'],
  ['İSG inceler', 'Her belge önizlemeden onaylanır; eksikler raporla firmaya döner.'],
  ['Sahada denetlenir', 'Onaylı iş QR ile doğrulanır, Five Star ile puanlanır.'],
];
const SHOTS = [['dashboard', 'Yönetim Paneli'], ['five_star', 'Five Star Denetimi'], ['evaluation', 'Firma Değerlendirme'], ['wizard', 'Çalışma İzni Sihirbazı']];

const Contact = ({ info }) => {
  const items = [[Phone, info.phone], [Mail, info.email], [MapPin, info.address]].filter(([, v]) => v);
  return (
    <section id="iletisim" className="bg-slate-950 px-6 py-20 text-white" data-testid="promo-contact">
      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
        <div>
          <p className="text-sm font-semibold uppercase tracking-widest text-orange-400">İletişim</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Sisteminizi birlikte kuralım</h2>
          <p className="mt-4 max-w-md text-slate-400">Demo talep edin veya sorularınızı iletin; ekibimiz en kısa sürede sizinle iletişime geçsin.</p>
          <div className="mt-8 space-y-4">
            {items.map(([I, v]) => <p key={v} className="flex items-center gap-3 text-slate-200"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500/15 text-orange-400"><I className="h-5 w-5" /></span>{v}</p>)}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#000616] p-8 lg:justify-self-end" data-testid="promo-owner">
          <p className="text-sm italic text-slate-400">by</p>
          <img src="/images/tiall-logo.png" alt="Tiall Software and Consulting" className="mt-2 h-20 w-auto" data-testid="promo-owner-logo" />
          <p className="mt-5 max-w-sm text-slate-200" data-testid="promo-owner-name">Tiall Teknoloji ve İnovasyon Yazılım, Yapay Zekâ, Eğitim ve Danışmanlık Limited Şirketi</p>
          <a href="https://www.tiall.com.tr" target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-orange-400 transition-colors hover:text-orange-300" data-testid="promo-owner-link">www.tiall.com.tr</a>
        </div>
      </div>
    </section>
  );
};

export default function PromoPage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState({});
  const [logo, setLogo] = useState(true);
  useEffect(() => { axios.get(`${API}/public/promo-info`).then(r => setInfo(r.data)).catch(() => {}); }, []);
  const toContact = () => document.getElementById('iletisim')?.scrollIntoView({ behavior: 'smooth' });
  return (
    <div className="min-h-screen bg-white" data-testid="promo-page">
      <header className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: 'url(/images/mall-wide.jpg)' }} />
        <div className="relative mx-auto max-w-6xl px-6">
          <nav className="flex items-center justify-between py-6">
            <span className="flex items-center gap-3 text-lg font-bold">
              {logo && <img src={`${API}/public/guide-logo`} alt="Logo" className="h-10 max-w-[180px] object-contain" onError={() => setLogo(false)} data-testid="promo-logo" />}
              <ShieldCheck className="h-6 w-6 text-orange-400" />AVM Belge Takip
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white" onClick={toContact} data-testid="promo-nav-contact">İletişim</Button>
              <Button className="bg-white text-slate-900 hover:bg-slate-200" onClick={() => navigate('/login')} data-testid="promo-nav-login">Giriş Yap</Button>
            </div>
          </nav>
          <div className="max-w-3xl py-20 sm:py-28">
            <p className="text-sm font-semibold uppercase tracking-widest text-orange-400">İSG Yönetim Platformu</p>
            <h1 className="mt-4 text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">AVM'nizdeki her çalışma, <span className="text-orange-400">güvenli ve kayıt altında.</span></h1>
            <p className="mt-6 max-w-2xl text-base text-slate-300 sm:text-lg">Yüklenici firmaların, personelin, iş ekipmanlarının ve çalışma izinlerinin İş Sağlığı ve Güvenliği mevzuatına uygun, dijital ve denetlenebilir yönetimi.</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button className="bg-orange-500 px-8 py-6 text-base hover:bg-orange-600" onClick={toContact} data-testid="promo-hero-demo">Demo Talep Et</Button>
              <Button variant="outline" className="border-white/30 bg-transparent px-8 py-6 text-base text-white hover:bg-white/10 hover:text-white" onClick={() => navigate('/login')} data-testid="promo-hero-login">Giriş Yap</Button>
            </div>
          </div>
        </div>
      </header>

      <section className="px-6 py-20" data-testid="promo-features">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-orange-500">Öne Çıkan Özellikler</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-bold text-slate-900 sm:text-4xl">Kâğıt, e-posta ve Excel yerine tek platform</h2>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map(([I, t, d], i) => (
              <div key={t} className="group rounded-2xl border border-slate-200 p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg" style={{ animation: `fadeInUp .5s ease ${i * 60}ms both` }} data-testid={`promo-feature-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-500 transition-colors group-hover:bg-orange-500 group-hover:text-white"><I className="h-6 w-6" /></span>
                <p className="mt-5 font-semibold text-slate-900">{t}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 px-6 py-20" data-testid="promo-steps">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-orange-500">Nasıl Çalışır</p>
          <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">Dört adımda güvenli saha</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-4">
            {STEPS.map(([t, d], i) => (
              <div key={t} className="relative">
                <span className="text-5xl font-bold text-orange-200">{String(i + 1).padStart(2, '0')}</span>
                <p className="mt-2 font-semibold text-slate-900">{t}</p>
                <p className="mt-1 text-sm text-slate-500">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20" data-testid="promo-shots">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-orange-500">Ekranlar</p>
          <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">Sade, hızlı ve her yerden erişilebilir</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {SHOTS.map(([n, t]) => (
              <figure key={n} className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
                <img src={img(n)} alt={t} loading="lazy" className="aspect-[16/9] w-full object-cover object-top" />
                <figcaption className="border-t bg-white px-4 py-3 text-sm font-medium text-slate-700">{t}</figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-6 rounded-2xl bg-orange-500 px-8 py-8 text-white">
            <QrCode className="h-12 w-12" />
            <p className="flex-1 text-lg font-semibold">Rol bazlı erişim, KVKK uyumu, sürümlü belge arşivi ve QR doğrulama ile tam denetlenebilirlik.</p>
            <Button className="bg-white text-orange-600 hover:bg-orange-50" onClick={toContact} data-testid="promo-cta-demo">Demo Talep Et</Button>
          </div>
        </div>
      </section>

      <Contact info={info} />
      <footer className="bg-slate-950 px-6 pb-8 text-center text-xs text-slate-500">© {new Date().getFullYear()} AVM Belge Takip Sistemi · Tüm hakları saklıdır</footer>
    </div>
  );
}
